import React from "react";
import type { ApolloClient } from "@apollo/client";
import { Col, Form, Row } from "react-bootstrap";
import { connect, type ConnectedProps } from "react-redux";
import { push } from "store/actions/router";
import {
  getCompanyInfo,
  updateRequiredOrderForm,
} from "actions/employerActions";
import Button from "components/Button";
import Alert from "components/Alert";

import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import IconSpinner from "components/IconSpinner";
import TermsModal from "pages/signUp/TermsModal";
import { employerConstants } from "actions/types";
import { getUserProfile, getUserState } from "actions/userActions";
import { userEmail, userProfile } from "store/selectors/user";
import { isEmpty } from "lodash";
import EmployerAdminNameForm from "components/EmployerAdminNameForm";
import type { RootState } from "types/state";
import type { EmployerRequiredStep } from "types/graphqlSchema";

const TYPE_TO_ORDER_FORM: Record<string, string> = {
  ICON_ORDER_FORM_AGREEMENT: "employerOrder",
  ISOLVED_ORDER_FORM_AGREEMENT: "isolvedOrder",
};

interface OwnProps {
  orderForm?: EmployerRequiredStep;
}

const actions = [
  employerConstants.GET_EMPLOYER_REQUIRED_STEPS,
  employerConstants.UPDATE_EMPLOYER_REQUIRED_ORDER_FORM,
];

const loadingSelector = createLoadingSelector(actions);

const errorSelector = createErrorSelector(actions);

const mapStateToProps = (state: RootState) => ({
  error: errorSelector(state),
  isFetching: loadingSelector(state),
  // selectors/user.ts types userEmail as string | undefined and userProfile
  // as {} — cast to the shape this component reads.
  userEmail: userEmail(state),
  userProfile: userProfile(state) as { firstName?: string; lastName?: string },
});

const mapDispatchToProps = {
  push,
  updateRequiredOrderForm,
  getUserState,
  getUserProfile,
  getCompanyInfo,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = WithApolloClient<OwnProps> & PropsFromRedux;

interface State {
  termsOfOrder: boolean;
  submitted: boolean;
  showTermsOfOrder: boolean;
  loading: boolean;
  theController: object;
}

class OrderFormAgreements extends React.Component<Props, State> {
  // pre-existing: never assigned, so the guarded setState below never runs
  _isMounted?: boolean;

  constructor(props: Props) {
    super(props);

    this.state = {
      termsOfOrder: false,
      submitted: false,
      showTermsOfOrder: false,
      loading: false,
      theController: {},
    };
  }

  async componentDidMount() {
    await Promise.all([
      this.props.getCompanyInfo(this.props.client as ApolloClient<object>),
      this.props.getUserProfile(this.props.client as ApolloClient<object>),
    ]);

    this.setState({ loading: false });
  }

  _submitOrderAgreements = (e: React.FormEvent) => {
    e.preventDefault();
    // update required updates
    void this.props
      .updateRequiredOrderForm(
        this.props.client as ApolloClient<object>,
        this.props.orderForm!
      )
      .then((result) => {
        if (result.success) {
          void this.props
            .getUserState(this.props.client as ApolloClient<object>)
            .then(() => {
              this.props.push("/dashboard");
            });
        }
      });

    if (this._isMounted) {
      this.setState({
        submitted: true,
      });
    }
  };

  _handleCheckBoxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // dynamic state key keyed by input name — preserve the original
    // computed-key setState behavior
    this.setState({
      [e.target.name]: e.target.checked,
    } as unknown as Pick<State, keyof State>);
  };

  render() {
    let content;
    const { firstName, lastName } = this.props.userProfile;

    if (this.state.loading) {
      content = <IconSpinner centered />;
    } else if (isEmpty(firstName) || isEmpty(lastName)) {
      // @ts-expect-error EmployerAdminNameForm (converted in a prior batch)
      // now requires onSuccess, but the original JS call site passed no
      // props — preserve the omission rather than introduce a new callback.
      content = <EmployerAdminNameForm />;
    } else {
      content = this.renderForm();
    }

    return content;
  }

  renderForm = () => {
    const termsInfo = {
      ...this.props.userProfile,
      userEmail: this.props.userEmail,
    };
    return (
      <div id="employer-terms">
        <TermsModal
          termsInfo={termsInfo}
          type={TYPE_TO_ORDER_FORM[this.props.orderForm!]}
          show={this.state.showTermsOfOrder}
          onClose={() => this.setState({ showTermsOfOrder: false })}
        />
        <Form noValidate onSubmit={this._submitOrderAgreements}>
          <div className="mega-container">
            <div className="step-container is-active" data-circle-percent="100">
              <section className="form-sec-2col">
                <section className="page-title-wrap">
                  <article className="text-cell">
                    <h1 className="page-title">Agreements</h1>
                    <p className="page-subtext">
                      Please review the following Order Form(s) agreements
                    </p>

                    <div id="form-employer-company-profile">
                      <Row className="mb-3">
                        <Form.Group
                          as={Col}
                          sm={12}
                          controlId="formBasictermsOfOrder"
                        >
                          <Form.Check
                            type="checkbox"
                            id="termsOfOrder"
                            name="termsOfOrder"
                            label="Order Form"
                            // @ts-expect-error pre-existing: boolean passed to
                            // Form.Check `value` (expects string|number) — inert
                            // coercion, preserved as-is
                            value={this.state.termsOfOrder}
                            onChange={this._handleCheckBoxChange}
                          />
                          <div
                            className="modal-link"
                            onClick={() => {
                              this.setState({
                                showTermsOfOrder: true,
                              });
                              window.analytics.track("Viewed Order Form");
                            }}
                          >
                            (View Order Form)
                          </div>
                        </Form.Group>
                      </Row>
                      <Row className="mb-3">
                        <Form.Group
                          as={Col}
                          sm={12}
                          controlId="formBasicFinalAgreementSpacing"
                        >
                          {" "}
                        </Form.Group>
                      </Row>

                      <div className="submit-row">
                        {this.props.error && (
                          <Alert type="error" msg={this.props.error} />
                        )}
                        <Button
                          name="submit"
                          disabled={!this.state.termsOfOrder}
                          withArrow={true}
                          btnLabel="Accept"
                          loading={this.props.isFetching}
                        />
                      </div>
                    </div>
                  </article>
                </section>
              </section>
            </div>
          </div>
        </Form>
      </div>
    );
  };
}

export default connector(withApollo<Props>(OrderFormAgreements));
